import { services } from '@config/services';
import { vendorApi, type FacilityAddress, type FacilityContact } from './_base';
import type { VendorFacility } from './facilities';

/**
 * Vendor-record endpoints (`/v1/vendors*`). Includes:
 *   - searchVendors  (list with VendorSearchArgs)
 *   - getVendorDetails / createVendor / updateVendor
 *   - getVendorPublicProfile (merchant-facing slice)
 *   - getVendorAuditLogs
 *
 * Facility sub-resource endpoints live in `./facilities.ts`. Vendor
 * config envelope lives in `./config.ts`.
 */

/**
 * Lookup row for the vendor-bulk endpoint, flattened for name resolution.
 * `GET /vendor/v1/vendors/bulk?bulk={id}&bulk={id}` returns
 * `{ bulk: [{ status, response: { vendor: {...} } }] }`; the transform keeps
 * only 200s and projects the id / name / facilities the routing view needs.
 */
export interface BulkVendor {
  id: string;
  name: string;
  facilities: Array<{ id: string; name: string }>;
}

interface VendorsBulkRawItem {
  status?: number;
  response?: {
    vendor?: {
      id?: string;
      vendorId?: string;
      name?: string;
      legalName?: string;
      facilities?: Array<{ id?: string; name?: string }>;
    };
  };
}

/** One row in the vendor audit-logs response. */
export interface VendorAuditLogEntry {
  requestId?: string;
  clientId?: string;
  userId?: string;
  action?: string;
  [key: string]: unknown;
}

export interface VendorAuditLogsResponse {
  events: VendorAuditLogEntry[];
}

/**
 * Merchant-facing public profile slice returned by
 * `GET /vendor/v1/vendors/{vendorId}/publicProfile`. Used by the plugin
 * Learn More page (`/admin/plugins/{pluginId}/about`). Server collapses
 * vendor email and portal URL out; only the narrow projection ships.
 *
 * `skuCount` is the only sub-section that can fail independently of the
 * parent fetch (its repo call is wrapped server-side as a partial-success
 * envelope). The other sections (aboutSummary / facilities / shipping)
 * succeed-or-fail with the request as a whole.
 */
export interface VendorPublicFacility {
  city?: string;
  countryCode?: string;
}

export interface VendorPublicShippingDetailedRow {
  carrierCode?: string;
  carrierName?: string;
  serviceMethod?: string;
  countryCode?: string;
  [key: string]: unknown;
}

export interface VendorPublicShipping {
  basic?: string[];
  detailed?: VendorPublicShippingDetailedRow[];
}

export interface VendorPublicSkuCount {
  /** "ok" | "error" — partial-success envelope; data is null when status==="error". */
  status?: 'ok' | 'error' | string;
  data?: { count?: number } | null;
}

export interface VendorPublicProfile {
  aboutSummary?: string;
  facilities?: VendorPublicFacility[];
  shipping?: VendorPublicShipping;
  skuCount?: VendorPublicSkuCount;
  /**
   * Whether the vendor has usable contact info on file. Rides the live
   * publicProfile GET — portalnew's `vendorServices.getVendorPublicProfile`
   * documents the slice as "shipping options, inline SKU count,
   * hasContactInfo bool". Drives the Contact Vendor CTA's enabled/disabled
   * state on the Learn More page; the server still defends a
   * submission with a 422 when contact info is actually missing.
   */
  hasContactInfo?: boolean;
  [key: string]: unknown;
}

/**
 * Vendor summary row returned by the list endpoint. The server does not
 * expose an enabled flag — the legacy UI hard-codes "Active" for every
 * row, and the port preserves that.
 */
export interface VendorFacilitySummary {
  id?: string;
  name?: string;
}

export interface VendorRow {
  id: string;
  shortId?: string;
  name: string;
  facilities: VendorFacilitySummary[];
}

/**
 * Full vendor record returned by `GET /v1/vendors/{vendorId}`. Includes the
 * facilities list (each row drives the FacilitiesView table) and the first
 * contact (DetailsView only shows index 0 — multi-contact support is a
 * separate port).
 */
export interface VendorDetailsRecord {
  /** Synonym for `id`; legacy responses include both, prefer `id` when
   * reading but write `vendorId` when forwarding to mutations. */
  vendorId?: string;
  id?: string;
  shortId?: string;
  name?: string;
  legalName?: string;
  url?: string;
  aboutSummary?: string;
  address?: FacilityAddress;
  contacts?: FacilityContact[];
  facilities?: VendorFacility[];
}

/** Form payload accepted by create/update vendor (matches legacy shape). */
export interface VendorInput {
  legalName: string;
  name: string;
  url?: string;
  aboutSummary?: string;
  address?: FacilityAddress;
  contacts?: FacilityContact[];
}

export interface VendorListResponse {
  vendors: VendorRow[];
  total: number;
}

/**
 * Server-contract sort columns for the vendors list. PascalCase is
 * required — the API rejects camelCase. Keep this union in sync with the
 * column ids consumed by the list page.
 */
export type VendorSortBy = 'ShortId' | 'Name' | 'NumLocations' | 'Status';

export interface VendorSearchArgs {
  query?: string | null;
  page?: number;
  pageSize?: number;
  sortBy?: VendorSortBy;
  sortDir?: 'asc' | 'desc';
  /** Sets the `disableXAccessHeader` URL flag (rarely used in this surface). */
  disableXAccessHeader?: boolean;
  /** Scopes results to one vendor (`&vendorId={id}` — matches the vendor's
   * id/shortId). Used to restrict pickers to a single vendor's org tree. */
  vendorId?: string;
}

/** One vendor cancel/reject reason (`GET /vendor/v1/reasons?type=Cancel`).
 * Hidden reasons are filtered out before display, mirroring the legacy modal. */
export interface VendorReason {
  name: string;
  isHidden?: boolean;
  [key: string]: unknown;
}

const vendorsApi = vendorApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * GET /vendor/v1/vendors?searchAll=&page=&pageSize=&sortBy=&sortAsc=
     *
     * Server-contract notes (verified against the legacy service call):
     * - `searchAll` is the search-string param name.
     * - `sortAsc` is the boolean direction (`'true'` / `'false'`), not
     *   `sortDir`.
     * - Sort column names are PascalCase (`ShortId | Name | NumLocations
     *   | Status`); the API rejects camelCase.
     * - `disableXAccessHeader` is a bare URL flag (no `=value`).
     * - `vendorId` scopes to a single vendor (vendor-service SearchVendorModel
     *   binds it and filters on the vendor's id/shortId):
     *   /vendor/v1/vendors?searchAll=&page=1&pageSize=250&sortBy=Name&sortAsc=true&vendorId={id}
     */
    searchVendors: builder.query<VendorListResponse, VendorSearchArgs>({
      query: ({
        query,
        page = 1,
        pageSize = 50,
        sortBy = 'Name',
        sortDir = 'asc',
        disableXAccessHeader,
        vendorId,
      }) => {
        const qs = new URLSearchParams();
        if (query) qs.set('searchAll', query);
        qs.set('page', String(page));
        qs.set('pageSize', String(pageSize));
        if (sortBy) qs.set('sortBy', sortBy);
        qs.set('sortAsc', String(sortDir === 'asc'));
        if (vendorId) qs.set('vendorId', vendorId);
        let url = `${services.vendor}/v1/vendors?${qs.toString()}`;
        if (disableXAccessHeader) url += '&disableXAccessHeader';
        return { url, method: 'GET' };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.vendors.map((v) => ({ type: 'VendorList' as const, id: v.id })),
              { type: 'VendorList' as const, id: 'LIST' },
            ]
          : [{ type: 'VendorList' as const, id: 'LIST' }],
    }),
    /**
     * GET /vendor/v1/vendors/{vendorId} — full vendor record incl.
     * facilities + first contact. Response is sometimes wrapped in
     * `{ vendor: {...} }`; the transform unwraps so callers read the
     * record directly.
     */
    getVendorDetails: builder.query<VendorDetailsRecord, { vendorId: string }>({
      query: ({ vendorId }) => ({
        url: `${services.vendor}/v1/vendors/${vendorId}`,
        method: 'GET',
      }),
      transformResponse: (response: VendorDetailsRecord | { vendor?: VendorDetailsRecord }) => {
        if (response && typeof response === 'object' && 'vendor' in response) {
          return ((response as { vendor?: VendorDetailsRecord }).vendor ?? {}) as VendorDetailsRecord;
        }
        return response as VendorDetailsRecord;
      },
      providesTags: (_r, _e, { vendorId }) => [{ type: 'Vendor', id: vendorId }],
    }),
    /**
     * Live shape (verified 2026-05-28 as super-admin viewing merchant
     * "Sales Channel 1" on /admin/plugins/{id}/about for a Vendor plugin):
     *   GET /vendor/v1/vendors/{vendorId}/publicProfile
     *
     * Returns the merchant-facing slice for the Learn More page —
     * about summary, city+country facilities, basic/detailed shipping,
     * inline skuCount partial-success envelope. No query params.
     */
    getVendorPublicProfile: builder.query<VendorPublicProfile, { vendorId: string }>({
      query: ({ vendorId }) => ({
        url: `${services.vendor}/v1/vendors/${encodeURIComponent(vendorId)}/publicProfile`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { vendorId }) => [
        { type: 'VendorPublicProfile', id: vendorId },
      ],
    }),
    /**
     * GET /vendor/v1/reasons?type=Cancel — vendor cancel/reject reasons
     * (powers the Reject Shipment modal dropdown). LIVE-confirmed on
     * portal.dev 2026-05-30 → 200 with rows {name, isHidden}. Response has
     * shipped both a bare array and a `{ reasons: [...] }` wrapper; normalise.
     */
    getVendorReasons: builder.query<VendorReason[], { type?: string }>({
      query: ({ type = 'Cancel' }) => ({
        url: `${services.vendor}/v1/reasons?type=${encodeURIComponent(type)}`,
        method: 'GET',
      }),
      transformResponse: (response: VendorReason[] | { reasons?: VendorReason[]; data?: VendorReason[] }) => {
        if (Array.isArray(response)) return response;
        return response.reasons ?? response.data ?? [];
      },
    }),
    /** POST /vendor/v1/vendors — returns the created vendor incl. its id. */
    createVendor: builder.mutation<VendorDetailsRecord, VendorInput>({
      query: (data) => ({
        url: `${services.vendor}/v1/vendors`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'VendorList', id: 'LIST' }],
    }),
    /**
     * PATCH /vendor/v1/vendors/{vendorId} — partial update (legacy parity).
     * MUST be PATCH, not PUT: the server replaces the whole record on PUT, and
     * this payload omits facilities/shipping, so a PUT silently wipes them.
     */
    updateVendor: builder.mutation<
      VendorDetailsRecord,
      { vendorId: string; data: VendorInput }
    >({
      query: ({ vendorId, data }) => ({
        url: `${services.vendor}/v1/vendors/${vendorId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_r, _e, { vendorId }) => [
        { type: 'Vendor', id: vendorId },
        { type: 'VendorList', id: vendorId },
        { type: 'VendorList', id: 'LIST' },
      ],
    }),
    /**
     * GET /vendor/v1/vendors/{vendorId}/auditLogs — vendor-wide audit log
     * (request id / client / user / action). Powers FacilityDetails →
     * History tab. Different shape from /history/v1 which feeds the
     * Vendor-level HistoryView.
     */
    getVendorAuditLogs: builder.query<VendorAuditLogsResponse, { vendorId: string }>({
      query: ({ vendorId }) => ({
        url: `${services.vendor}/v1/vendors/${vendorId}/auditLogs`,
        method: 'GET',
      }),
    }),
    /**
     * GET /vendor/v1/vendors/bulk?bulk={id}&bulk={id} — resolve multiple
     * vendors (name + facilities) by id. Live envelope:
     * `{ bulk: [{ status, response: { vendor: {...} } }] }`. Ported from
     * legacy `vendorServices.getVendorsBulk`. Used by the routing strategy
     * view to label group vendors/facilities. Skips when `vendorIds` is empty.
     */
    getVendorsBulk: builder.query<BulkVendor[], { vendorIds: string[] }>({
      query: ({ vendorIds }) => {
        // Build the query string only when there are ids — avoids a trailing '?'.
        const qs = vendorIds.length
          ? `?${vendorIds.map((id) => `bulk=${encodeURIComponent(id)}`).join('&')}`
          : '';
        return {
          url: `${services.vendor}/v1/vendors/bulk${qs}`,
          method: 'GET',
        };
      },
      transformResponse: (response: { bulk?: VendorsBulkRawItem[] } | VendorsBulkRawItem[]): BulkVendor[] => {
        const rows = Array.isArray(response) ? response : (response.bulk ?? []);
        return rows
          .filter((item) => item.status === 200 && item.response?.vendor)
          .map((item) => {
            const v = item.response!.vendor!;
            return {
              id: v.id ?? v.vendorId ?? '',
              name: v.name ?? v.legalName ?? '',
              facilities: (v.facilities ?? []).map((f) => ({ id: f.id ?? '', name: f.name ?? '' })),
            };
          });
      },
    }),
  }),
});

export const {
  useSearchVendorsQuery,
  useGetVendorDetailsQuery,
  useGetVendorPublicProfileQuery,
  useCreateVendorMutation,
  useUpdateVendorMutation,
  useGetVendorAuditLogsQuery,
  useGetVendorsBulkQuery,
  useGetVendorReasonsQuery,
} = vendorsApi;
